import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { API_BASE_URL } from '../config';

// Generate or retrieve session ID
export const getOrCreateSessionId = () => {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

// Get hardware-specific device ID
export const getDeviceId = () => {
  let deviceId = localStorage.getItem('device_id');
  
  if (!deviceId) {
    const hardwareInfo = [
      navigator.userAgent,
      navigator.platform,
      navigator.hardwareConcurrency || 'unknown',
      window.screen.width,
      window.screen.height,
      window.screen.colorDepth,
      window.devicePixelRatio,
      navigator.language,
      Intl.DateTimeFormat().resolvedOptions().timeZone
    ].join('||');

    let hash = 0;
    for (let i = 0; i < hardwareInfo.length; i++) {
      const char = hardwareInfo.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    deviceId = hash.toString(16) + Date.now().toString(36);
    localStorage.setItem('device_id', deviceId);
  }
  
  return deviceId;
};

// Store user identification data in cookies
export const storeUserIdentification = (user, token) => {
  
  // Generate chat ID if not exists
  let chatId = Cookies.get('chat_id') || uuidv4();
  let userId = Cookies.get('user_id') || uuidv4();
  const deviceId = getDeviceId();
  const sessionId = getOrCreateSessionId();
  
  // Only set cookies if they don't exist to prevent expiry extension
  if (!Cookies.get('chat_id')) {
    Cookies.set('chat_id', chatId, { secure: true, sameSite: 'none', expires: 1 }); // 1 day
  }
  if (!Cookies.get('user_id')) {
    Cookies.set('user_id', userId, { secure: true, sameSite: 'none', expires: 1 }); // 1 day
  }
  if (!Cookies.get('device_id')) {
    Cookies.set('device_id', deviceId, { secure: true, sameSite: 'none', expires: 1 }); // 1 day
  }
  if (!Cookies.get('session_id')) {
    Cookies.set('session_id', sessionId, { secure: true, sameSite: 'none', expires: 1 }); // 1 day
  }
  
  return { chat_id: chatId, user_id: user.uid, device_id: deviceId, session_id: sessionId };
};

class VectorClock {
    constructor(nodeId) {
        this.nodeId = nodeId;
        const currentTime = Date.now();  // millisecond precision
        this.clock = {
            [nodeId]: currentTime
        };
    }

    increment() {
        const currentTime = Date.now();
        const currentValue = this.clock[this.nodeId] || currentTime;
        // Ensure the clock always moves forward
        this.clock[this.nodeId] = Math.max(currentTime, currentValue + 1);
    }

    merge(otherClock) {
        const currentTime = Date.now();
        for (const [node, time] of Object.entries(otherClock)) {
            const existingValue = this.clock[node] || currentTime;
            this.clock[node] = Math.max(time, existingValue);
        }
        
        // Ensure local node's time is at least current
        this.clock[this.nodeId] = Math.max(
            this.clock[this.nodeId] || currentTime,
            currentTime
        );
    }

    toJSON() {
        return this.clock;
    }
}

// Initialize vector clock with device ID as node identifier
const vectorClock = new VectorClock(getDeviceId());

const processSyncedMessages = (messages) => {
    console.log('Processing synced messages:', messages);
    
    // Keep track of processed message content and timestamps to avoid duplicates
    const processedContent = new Set();
    const processedTimestamps = new Set();
    
    const processedMessages = messages.flatMap(message => {
        console.log('Processing message:', message);
        const isAI = ['AI', 'ADMIN'].includes(message.sent_by);
        const messageContent = typeof message.content === 'string' ? 
            JSON.parse(message.content) : message.content;
        console.log('Message content:', messageContent);
        
        if (isAI) {
            console.log('Processing AI message with timestamp:', message.server_timestamp);
            const systemPrompt = messageContent.system_prompt || [];
            console.log('System prompt:', systemPrompt);
            
            // First, collect all shoe recommendations
            const shoeRecommendations = systemPrompt
                .filter(item => item.type === "ShoeRecommendationEntity")
                .map(item => {
                    console.log('Processing shoe item:', item);
                    
                    // Handle both image_ids array and product_url string
                    let allImages = [];
                    if (item.value.image_ids && Array.isArray(item.value.image_ids)) {
                        allImages = item.value.image_ids.filter(url => url && url.startsWith('http'));
                    } else if (item.value.product_url) {
                        allImages = item.value.product_url.split(',')
                            .map(s => s.trim())
                            .filter(url => url && url.startsWith('http'));
                    }
                    
                    return {
                        id: item.value.shoe_id,
                        title: item.value.name,
                        description: item.value.description,
                        price: item.value.price,
                        compare_at_price: item.value.compare_at_price,
                        productUrl: allImages[0] || '', // First image as primary
                        allImages: allImages, // All images for modal
                        product_url: item.value.product_url,
                        image_ids: item.value.image_ids,
                        brand: item.value.brand,
                        name: item.value.name,
                        size: item.value.size,
                        citations: item.value.citations
                    };
                });
            console.log('Total shoe recommendations collected:', shoeRecommendations.length);
            
            console.log('Collected shoe recommendations:', shoeRecommendations);
            
            // Then process each message
            return systemPrompt.map((item, index) => {
                const timestamp = Object.values(message.server_timestamp)[0] + index;
                console.log(`Processing prompt item ${index}:`, item);
                
                const messageKey = `${timestamp}-${item.type}-${JSON.stringify(item.value)}`;
                if (processedContent.has(messageKey)) {
                    return null;
                }
                processedContent.add(messageKey);

                switch (item.type) {
                    case "RecommendationExplanation":
                        return {
                            message: item.value,
                            type: "text",
                            sender: "AI",
                            timestamp: timestamp
                        };
                    case "ShoeRecommendationEntity":
                        // Only create carousel message once, when processing the first shoe
                        if (shoeRecommendations.length > 0 && item.value.shoe_id === shoeRecommendations[0].id) {
                            console.log('Creating carousel message with items:', shoeRecommendations.length);
                            console.log('First item in carousel:', shoeRecommendations[0]);
                            return {
                                type: "carousel",
                                sender: "AI",
                                timestamp: timestamp,
                                items: shoeRecommendations  // Pass all recommendations as items
                            };
                        }
                        return null;  // Skip other shoe recommendations
                    case "FurtherInteraction":
                        return {
                            message: item.value,
                            type: "prompt",
                            sender: "AI",
                            timestamp: timestamp
                        };
                    default:
                        return null;
                }
            }).filter(Boolean);
        } else {
            // For user messages, check if we've already processed this timestamp or content
            const timestamp = Object.values(message.server_timestamp)[0];
            const userValue = messageContent.user_input && messageContent.user_input[0] && messageContent.user_input[0].value;
            const userKey = `${timestamp}-${userValue}`;
            if (processedTimestamps.has(timestamp) || processedContent.has(userKey)) {
                return null;
            }
            processedTimestamps.add(timestamp);
            processedContent.add(userKey);
            // User message processing
            return [{
                message: userValue,
                type: "text",
                sender: "USER",
                timestamp: timestamp
            }];
        }
    });

    console.log('Final processed messages:', processedMessages);
    return processedMessages.sort((a, b) => a.timestamp - b.timestamp);
};

const processAIResponse = (aiResponse) => {
    console.log('Processing AI response:', aiResponse);
    
    if (!aiResponse.content || !Array.isArray(aiResponse.content)) {
        console.log('No content or invalid content in AI response');
        return null;
    }

    // Process the system prompt content
    const systemPrompt = aiResponse.content;
    console.log('System prompt:', systemPrompt);
    
    // First, collect all shoe recommendations
    const shoeRecommendations = systemPrompt
        .filter(item => item.type === "ShoeRecommendationEntity")
        .map(item => {
            console.log('Processing shoe item:', item);
            
            // Handle both image_ids array and product_url string
            let allImages = [];
            if (item.value.image_ids && Array.isArray(item.value.image_ids)) {
                allImages = item.value.image_ids.filter(url => url && url.startsWith('http'));
            } else if (item.value.product_url) {
                allImages = item.value.product_url.split(',')
                    .map(s => s.trim())
                    .filter(url => url && url.startsWith('http'));
            }
            
            return {
                id: item.value.shoe_id,
                title: item.value.name,
                description: item.value.description,
                price: item.value.price,
                compare_at_price: item.value.compare_at_price,
                productUrl: allImages[0] || '', // First image as primary
                allImages: allImages, // All images for modal
                product_url: item.value.product_url,
                image_ids: item.value.image_ids,
                brand: item.value.brand,
                name: item.value.name,
                size: item.value.size,
                citations: item.value.citations
            };
        });
    console.log('Total shoe recommendations collected:', shoeRecommendations.length);
    
            // Process each prompt item
        const processedItems = systemPrompt.map((item, index) => {
            // Convert server timestamp from seconds to milliseconds, or use current time as fallback
            const serverTimestamp = aiResponse.timestamp ? aiResponse.timestamp * 1000 : Date.now();
            const timestamp = serverTimestamp + index;
            console.log(`Processing prompt item ${index}:`, item);
            console.log(`Server timestamp: ${aiResponse.timestamp}, converted to ms: ${serverTimestamp}, final: ${timestamp}`);
            
            switch (item.type) {
                case "RecommendationExplanation":
                    return {
                        message: item.value,
                        type: "text",
                        sender: "AI",
                        timestamp: timestamp
                    };
                case "ShoeRecommendationEntity":
                    // Create carousel message only once when processing the first shoe
                    if (shoeRecommendations.length > 0 && item.value.shoe_id === shoeRecommendations[0].id) {
                        console.log('Creating carousel message with items:', shoeRecommendations.length);
                        return {
                            type: "carousel",
                            sender: "AI",
                            timestamp: timestamp,
                            items: shoeRecommendations  // Pass all recommendations as items
                        };
                    }
                    return null;  // Skip other shoe recommendations to avoid duplicate carousels
                case "FurtherInteraction":
                    return {
                        message: item.value,
                        type: "prompt",
                        sender: "AI",
                        timestamp: timestamp
                    };
                default:
                    return null;
            }
        }).filter(Boolean);

    console.log('Final processed AI response:', processedItems);
    
    // Return all processed items to ensure all message types are displayed
    return processedItems;
};

export const fetchNewMessages = async (chatId, onNewMessages) => {
    try {
        const userId = Cookies.get('user_id');
        const sessionId = Cookies.get('session_id');
        const deviceId = Cookies.get('device_id');
        const lastSyncedTimestamp = Cookies.get('last_synced_server_timestamp') || '{}';

        // Increment vector clock before request
        vectorClock.increment();

        const response = await fetch(`${API_BASE_URL}/personal-shopper/chat/${chatId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'role': 'User',
                'X-Cookie': `user_id=${userId}; session_id=${sessionId}; chat_id=${chatId}; device_id=${deviceId}`,
                'X-Vector-Clock': JSON.stringify(vectorClock.toJSON()),
                'last-synced-server-timestamp': lastSyncedTimestamp
            },
            credentials: 'include'
        });

        console.log(`GET API Response Status: ${response.status} - ${response.statusText}`);
        
        if (!response.ok) {
            throw new Error(`API request failed: ${response.status} - ${response.statusText}`);
        }

        // Update vector clock from server response
        const serverClock = JSON.parse(response.headers.get('X-Vector-Clock') || '{}');
        vectorClock.merge(serverClock);

        const lastSyncedTimestampFromHeader = response.headers.get('X-Last-Synced-Timestamp');
        console.log('Received timestamp:', lastSyncedTimestampFromHeader);
        
        if (lastSyncedTimestampFromHeader) {
            try {
                Cookies.remove('last_synced_server_timestamp'); // Clear existing cookie first
                Cookies.set('last_synced_server_timestamp', lastSyncedTimestampFromHeader, { 
                    secure: true, 
                    sameSite: 'none',
                    expires: 1  // Set expiry to 1 day to match other cookies
                });
                console.log('Saved timestamp to cookie:', Cookies.get('last_synced_server_timestamp'));
            } catch (e) {
                console.error('Error setting timestamp cookie:', e);
            }
        }
        
        const data = await response.json();
        console.log('Received GET response:', data);
        
        if (data.synced_messages && data.synced_messages.length > 0) {
            console.log('Processing synced messages from GET request');
            const processedMessages = processSyncedMessages(data.synced_messages);
            if (processedMessages && processedMessages.length > 0) {
                console.log('Calling onNewMessages with:', processedMessages);
                onNewMessages(processedMessages);
            }
        }

        return data;
    } catch (error) {
        console.error('Polling Error:', error);
        throw error;
    }
};

// Modified chatWithAI function to include polling
export const chatWithAI = async (input, onNewMessages) => {
    try {
        // Generate temporary user and token if not authenticated
        const tempUser = {
            uid: uuidv4()
        };
        
        // Store user identification
        storeUserIdentification(tempUser);
        
        // Get values from cookies (will now use the newly stored values)
        const userId = Cookies.get('user_id');
        const sessionId = Cookies.get('session_id');
        const chatId = Cookies.get('chat_id');
        const deviceId = Cookies.get('device_id');
        const lastSyncedTimestamp = Cookies.get('last_synced_server_timestamp') || '{}';
        
        // Increment vector clock before request
        vectorClock.increment();
        
        const response = await fetch(`${API_BASE_URL}/personal-shopper/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'role': 'User',
                'X-Cookie': `user_id=${userId}; session_id=${sessionId}; chat_id=${chatId}; device_id=${deviceId}`,
                'X-Vector-Clock': JSON.stringify(vectorClock.toJSON()),
                'last-synced-server-timestamp': lastSyncedTimestamp
            },
            credentials: 'include',
            body: JSON.stringify({
                user_id: userId,
                session_id: sessionId,
                defined_parameters: null,
                device_id: deviceId,
                chat_id: chatId,
                user_input: [{
                    type: "string",
                    value: input
                }],
                timestamp: Math.floor(Date.now() / 1000),  // Convert to seconds
                chat_history: []
            })
        });

        console.log(`POST API Response Status: ${response.status} - ${response.statusText}`);
        
        if (!response.ok) {
            throw new Error(`API request failed: ${response.status} - ${response.statusText}`);
        }

        // Check if we need to start polling (status 202, payment link detected, or escalation requested)
        let shouldStartPolling = false;
        let isPaymentPolling = false; // Flag to distinguish payment polling from regular polling
        let isEscalationPolling = false; // Flag to distinguish escalation polling from regular polling
        let pollTimeout = null;
        
        if (response.status === 202) {
            console.log('Status 202 received - starting polling for async response');
            shouldStartPolling = true;
        }

        // Update vector clock from server response
        const serverClock = JSON.parse(response.headers.get('X-Vector-Clock') || '{}');
        vectorClock.merge(serverClock);

        const lastSyncedTimestampFromHeader = response.headers.get('X-Last-Synced-Timestamp');
        console.log('Received timestamp:', lastSyncedTimestampFromHeader);
        
        if (lastSyncedTimestampFromHeader) {
            try {
                Cookies.remove('last_synced_server_timestamp'); // Clear existing cookie first
                Cookies.set('last_synced_server_timestamp', lastSyncedTimestampFromHeader, { 
                    secure: true, 
                    sameSite: 'none',
                    expires: 1  // Set expiry to 1 day to match other cookies
                });
                console.log('Saved timestamp to cookie:', Cookies.get('last_synced_server_timestamp'));
            } catch (e) {
                console.error('Error setting timestamp cookie:', e);
            }
        }

        const data = await response.json();

        // Helper function to detect payment links
        const hasPaymentLink = (text) => {
            return text.includes('mercury-t2.phonepe.com/transact') || 
                   text.includes('mercury.phonepe.com/transact') ||
                   text.includes('phonepe.com/transact') ||
                   text.includes('phonepe.com') ||
                   text.includes('mercury.phonepe.com');
        };
        
        // Check for payment links in the response to determine if polling is needed
        const responseText = JSON.stringify(data);
        if (hasPaymentLink(responseText)) {
            console.log('Payment link detected in response - starting polling for payment response');
            shouldStartPolling = true;
            isPaymentPolling = true; // Mark this as payment polling
        }

        // Handle immediate AI response from the new endpoint pattern
        if (data.status === 'done' && data.result) {
            // Check for escalation_requested in metadata
            if (data.result.metadata && data.result.metadata.escalation_requested === true) {
                console.log('🚨 Escalation requested detected in metadata - starting indefinite polling');
                console.log('📊 Metadata:', JSON.stringify(data.result.metadata, null, 2));
                shouldStartPolling = true;
                isEscalationPolling = true; // Mark this as escalation polling
            }

            // Process the immediate AI response
            const aiResponse = {
                sent_by: 'AI',
                content: data.result.system_prompt || [],
                timestamp: data.result.timestamp,
                metadata: data.result.metadata
            };

            // Convert the AI response to the expected message format
            const processedAIResponse = processAIResponse(aiResponse);
            console.log('Processed AI response:', processedAIResponse);
            if (processedAIResponse && processedAIResponse.length > 0) {
                // Call onNewMessages immediately to stop typing indicator
                onNewMessages(processedAIResponse);
                
                // Check if any of the processed messages contain payment links
                const hasPaymentLinkInMessages = processedAIResponse.some(msg => {
                    const messageText = JSON.stringify(msg);
                    return hasPaymentLink(messageText);
                });
                
                if (hasPaymentLinkInMessages) {
                    console.log('Payment link detected in processed messages - starting polling');
                    shouldStartPolling = true;
                    isPaymentPolling = true; // Mark this as payment polling
                }
            }

            // If we need to start polling, don't return immediately
            if (!shouldStartPolling) {
                return {
                    messages: processedAIResponse,
                    isImmediateResponse: true
                };
            }
        }

        // Fallback: Process synced messages if they exist (for backward compatibility)
        if (data.synced_messages && data.synced_messages.length > 0) {
            // Filter out the user's input message from synced_messages
            const filteredMessages = data.synced_messages.filter(msg => {
                if (msg.sent_by === 'USER') {
                    const content = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
                    return content.user_input[0].value !== input;
                }
                return true;
            });
            
            if (filteredMessages.length > 0) {
                const processedMessages = processSyncedMessages(filteredMessages);
                onNewMessages(processedMessages);
                return {
                    messages: processedMessages,
                    isImmediateResponse: false
                };
            }
        }

        // Start polling if needed (status 202, payment link detected, or escalation requested)
        if (shouldStartPolling) {
            if (isEscalationPolling) {
                console.log('🔄 Starting indefinite polling for escalation response');
                console.log('⏰ No timeout will be set - polling will continue indefinitely');
            } else {
                console.log('🔄 Starting polling for async response or payment updates');
            }
            
            // Set timeout for polling (NO timeout for escalation polling)
            const POLL_INTERVAL = 2000; // Poll every 2 seconds
            
            // Only set timeout for non-escalation polling
            if (!isEscalationPolling) {
                const POLL_TIMEOUT = 13 * 60 * 1000; // 13 minutes in milliseconds
                pollTimeout = setTimeout(() => {
                    console.log('Polling timeout reached (13 minutes) - stopping polling');
                    shouldStartPolling = false;
                    clearInterval(pollInterval); // Clear the interval when timeout is reached
                }, POLL_TIMEOUT);
            } else {
                console.log('⏰ No timeout set for escalation polling - will continue indefinitely');
            }
            
            // Track the last message timestamp to detect truly new messages
            let lastMessageTimestamp = Date.now();
            
            // Start polling
            const pollInterval = setInterval(async () => {
                if (!shouldStartPolling) {
                    clearInterval(pollInterval);
                    if (pollTimeout) {
                        clearTimeout(pollTimeout);
                        pollTimeout = null;
                    }
                    return;
                }
                
                // For escalation polling, add extra logging to show it's running indefinitely
                if (isEscalationPolling) {
                    console.log('🔄 Escalation polling active - checking for new messages...');
                }
                
                try {
                    console.log('Polling for new messages...');
                    const pollResponse = await fetchNewMessages(chatId, onNewMessages);
                    
                    // Only consider messages that are newer than our last known message
                    if (pollResponse && pollResponse.synced_messages && pollResponse.synced_messages.length > 0) {
                        const newMessages = pollResponse.synced_messages.filter(msg => {
                            const msgTimestamp = Object.values(msg.server_timestamp)[0];
                            return msgTimestamp > lastMessageTimestamp;
                        });
                        
                        if (newMessages.length > 0) {
                            // For payment polling, continue polling but display new messages
                            if (isPaymentPolling) {
                                console.log(`Payment polling: Found ${newMessages.length} new messages, displaying but continuing to poll...`);
                                
                                // Process and display the new messages
                                const processedMessages = processSyncedMessages(newMessages);
                                if (processedMessages && processedMessages.length > 0) {
                                    onNewMessages(processedMessages);
                                }
                                
                                // Update timestamp but DON'T stop polling for payment scenarios
                                lastMessageTimestamp = Math.max(...pollResponse.synced_messages.map(msg => 
                                    Object.values(msg.server_timestamp)[0]
                                ));
                            } else if (isEscalationPolling) {
                                // For escalation polling, continue polling indefinitely but display new messages
                                console.log(`🚨 Escalation polling: Found ${newMessages.length} new messages, displaying but continuing to poll indefinitely...`);
                                console.log('🔄 Escalation polling will continue until manually stopped');
                                
                                // Process and display the new messages
                                const processedMessages = processSyncedMessages(newMessages);
                                if (processedMessages && processedMessages.length > 0) {
                                    onNewMessages(processedMessages);
                                }
                                
                                // Update timestamp but DON'T stop polling for escalation scenarios
                                lastMessageTimestamp = Math.max(...pollResponse.synced_messages.map(msg => 
                                    Object.values(msg.server_timestamp)[0]
                                ));
                            } else {
                                // For regular polling (status 202), stop when new messages arrive
                                console.log(`Regular polling: Found ${newMessages.length} truly new messages, stopping polling`);
                                shouldStartPolling = false;
                                clearInterval(pollInterval);
                                if (pollTimeout) {
                                    clearTimeout(pollTimeout);
                                    pollTimeout = null;
                                }
                                
                                // Process and display the new messages
                                const processedMessages = processSyncedMessages(newMessages);
                                if (processedMessages && processedMessages.length > 0) {
                                    onNewMessages(processedMessages);
                                }
                            }
                        } else {
                            console.log('No new messages found, continuing to poll...');
                        }
                    } else {
                        console.log('No synced messages in response, continuing to poll...');
                    }
                } catch (error) {
                    console.error('Polling error:', error);
                    // Continue polling on error, don't stop
                }
            }, POLL_INTERVAL);
            
            // Return empty response since we're polling
            return {
                messages: [],
                isImmediateResponse: false,
                isPolling: true
            };
        }
        
        return {
            messages: [],
            isImmediateResponse: false
        };  // No messages to return
    } catch (error) {
        console.error('API Error:', error);
        throw error;
    }
};

export const submitExploreResponse = async (userResponse) => {
    // Mock response without API call
    return {
        type: "ExploreEntity",
        value: {
            shoe_id: "123",
            image_ids: ["default.png"],
            brand: "Unknown",
            name: "Unknown",
            description: "Unknown",
            product_url: "https://example.com",
            price: 0.0
        }
    };
};